import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { Reveal } from "../components/Reveal";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("cat") || "all";
  const [active, setActive] = useState(initial);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (active === "all") {
      params.delete("cat");
    } else {
      params.set("cat", active);
    }
    setParams(params, { replace: true });
    // eslint-disable-next-line
  }, [active]);

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => active === "all" || p.category === active)
      .filter((p) =>
        query.trim()
          ? (p.name + " " + p.tagline + " " + p.composition).toLowerCase().includes(query.trim().toLowerCase())
          : true
      );
  }, [active, query]);

  return (
    <div data-testid="page-products">
      {/* Page header */}
      <section className="bg-brand-surface border-b border-brand-line py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">Our Catalog</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-brand-ink tracking-tight mb-3">Our Products</h1>
          <p className="text-base text-brand-ink/65 max-w-2xl">Complete range of veterinary medicines for cattle, buffalo, goat, sheep & poultry — formulated for Indian conditions.</p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white sticky top-20 z-30 border-b border-brand-line" data-testid="products-filter-bar">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`filter-tab-${c.id}`}
                className={`font-heading font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-md border transition-all ${
                  active === c.id
                    ? "bg-brand-orange text-white border-brand-orange"
                    : "bg-white text-brand-ink/70 border-brand-line hover:border-brand-orange hover:text-brand-orange"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="products-search"
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2.5 rounded-md border border-brand-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-brand-ink/60" data-testid="no-products">No products match your filter.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <Reveal key={p.slug} delay={i * 40}>
                  <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="product-card group block bg-white border border-brand-line rounded-lg overflow-hidden h-full">
                    <div className="aspect-[4/3] bg-brand-surface overflow-hidden">
                      <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-heading font-bold tracking-[0.18em] uppercase text-brand-orange">{p.categoryLabel}</span>
                        <span className="text-[10px] font-heading font-bold tracking-wider uppercase px-2 py-0.5 rounded bg-brand-green/10 text-brand-green">In Stock</span>
                      </div>
                      <h3 className="font-heading font-bold text-base uppercase tracking-wide text-brand-ink mb-1.5 leading-snug">{p.name}</h3>
                      <p className="text-sm text-brand-ink/65 line-clamp-2 mb-4">{p.tagline}</p>
                      <div className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-brand-orange">
                        View Details <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
